<template>
  <div>
    <header-bar>
      {{ type === 'album' ? 'アルバム（2曲以上）' : 'シングル' }}配信
    </header-bar>
    <div class="page-block">
      <div class="page-block__heading page-block__heading--withbar">
        <h3 class="page-block__title">リリース情報登録 - 楽曲情報の登録</h3>
        <release-type-label :type="type" />
      </div>
      <div class="page-block__inner">
        <release-step-area :index="2">
          楽曲情報の登録
        </release-step-area>
        <alert-view
          v-if="!realtimeValidation"
          :errors="errors" />
        <form
          class="form--error"
          @submit.prevent="onRegisterClicked()">
          <div class="form-wrap">
            <div class="form-group">
              <div class="form-label">
                <label class="control-label">楽曲区分
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.classification)"
                  class="sp-tooltip-icon">
                </i>
                <i
                  v-tooltip="pcTooltipConfig(tooltipMessages.classification)"
                  class="tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="classification_original">
                      <input
                        id="classification_original"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :value="1"
                        :disabled="disableTitleChange"
                        checked
                        @change="onChangeClassification(1)">
                      <div class="option-input"></div>
                      オリジナル
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_cover">
                      <input
                        id="classification_cover"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="2"
                        @change="onChangeClassification(2)">
                      <div class="option-input"></div>
                      カバー
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_arrange">
                      <input
                        id="classification_arrange"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="3"
                        @change="onChangeClassification(3)">
                      <div class="option-input"></div>
                      アレンジ
                    </label>
                  </div>
                  <div class="radio">
                    <label for="classification_public">
                      <input
                        id="classification_public"
                        v-model="item.classification"
                        type="radio"
                        name="classification"
                        :disabled="disableTitleChange"
                        :value="4"
                        @change="onChangeClassification(4)">
                      <div class="option-input"></div>
                      パブリックドメイン
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_ja">タイトル
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_ja)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_ja"
                  v-model="item.title_ja"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_ja)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_ja')"
                  @input="findVersioWords()">
                <p
                  v-if="foundVersioWords"
                  class="error-text">
                  バージョン(Ver. / Remix / 2022 Remastered / Cover / Live 等)はタイトル欄では無くバージョン欄に登録してください。
                </p>
                <p
                  v-if="errors.title_ja"
                  class="error-text">
                  {{ errors.title_ja[0] }}
                </p>
                <p
                  v-if="hasInitialDigit"
                  class="notice-text">
                  タイトルの頭に数字が入っている場合は、トラック番号とみなされ変更が入る場合がございます。
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_kana">タイトル（カナ表記）
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_kana)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_kana"
                  v-model="item.title_kana"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_kana)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_kana')">
                <p
                  v-if="errors.title_kana"
                  class="error-text">
                  {{ errors.title_kana[0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="title_ja">タイトル（アルファベット表記）
                  <span class="req">＊</span>
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.title_en)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <input
                  id="title_en"
                  v-model="item.title_en"
                  v-tooltip="pcTooltipConfig(tooltipMessages.title_en)"
                  class="form-control"
                  type="text"
                  :disabled="disableTitleChange"
                  @blur="onFieldChanged('title_en')">
                <p
                  v-if="errors.title_en"
                  class="error-text">
                  {{ errors.title_en[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="!hideVersion"
              class="form-group-wrap">
              <div class="form-group-wrap__version-title js-tooltip">
                バージョン
                <i
                  v-tooltip="spTooltipConfig(
                    isBtoB ? tooltipMessages.versions_b2b : tooltipMessages.versions_b2c
                  )"
                  class="sp-tooltip-icon">
                </i>
                <i
                  v-tooltip="pcTooltipConfig(
                    isBtoB ? tooltipMessages.versions_b2b : tooltipMessages.versions_b2c
                  )"
                  class="tooltip-icon">
                </i>
              </div>
              <div class="form-group-wrap__example">
                例： Live / Remix / 2022 Remastered / Cover / Ver. など
                <div
                  class="form-item-text"
                  style="margin-top:1em;">
                  ライブ録音の楽曲では、通常録音との区別をするため、バージョンに「Live」または<br />
                  「ライブ」をご登録いただく必要がございます。
                </div>
              </div>
              <div
                v-if="version_status">
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="version_ja">バージョン
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_ja)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_ja"
                      v-model="item.version_ja"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_ja)"
                      class="form-control"
                      type="text"
                      placeholder="(例)Acoustic. Ver"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_ja', 'version_ja'])">
                    <div
                      v-if="errors.version_ja"
                      class="error-text">
                      <p
                        v-for="message in errors.version_ja[0].split('\n')"
                        :key="message">
                        {{ message }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="version_kana">バージョン（カナ表記）
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_kana)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_kana"
                      v-model="item.version_kana"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_kana)"
                      class="form-control"
                      type="text"
                      placeholder="(例)アコースティックバージョン"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_kana', 'version_kana'])">
                    <p
                      v-if="errors.version_kana"
                      class="error-text">
                      {{ errors.version_kana[0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="title_en">バージョン（アルファベット表記）
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.version_en)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      id="version_en"
                      v-model="item.version_en"
                      v-tooltip="pcTooltipConfig(tooltipMessages.version_en)"
                      class="form-control"
                      type="text"
                      placeholder="(例)Acoustic. Ver"
                      :disabled="disableTitleChange"
                      @blur="onFieldChanged(['title_en', 'version_en'])">
                    <div
                      v-if="errors.version_en"
                      class="error-text">
                      <p
                        v-for="message in errors.version_en[0].split('\n')"
                        :key="message">
                        {{ message }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!disableTitleChange"
                class="form-group-wrap__btn-wrap">
                <button
                  v-if="!version_status"
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onVersionAddClicked">
                  追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  v-if="version_status"
                  type="button"
                  class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                  @click.prevent="onVersionDeleteClicked">
                  削除
                </button>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">メインアーティスト</div>
              <div
                v-for="(artist, index) in item.main_artists"
                :key="index">
                <div v-if="artist.type !==3">
                  <div
                    :class="{ 'not-first-space': index === 0 }"
                    class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_ja_${index}`">アーティスト名
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_ja_${index}`"
                        v-model="artist.name_ja"
                        v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_ja)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id && artistType !== 2"
                        @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_ja`][0] }}
                      </p>
                      <p
                        v-else-if="mainArtistWarnErrors[`main_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ mainArtistWarnErrors[`main_artists.${index}.name_ja`] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_kana_${index}`">アーティスト名（カナ表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_kana)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_kana_${index}`"
                        v-model="artist.name_kana"
                        v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_kana)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id && artistType !== 2"
                        @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_kana`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_kana`][0] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`main_artist_name_en_${index}`">アーティスト名（アルファベット表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_en)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`main_artist_name_en_${index}`"
                        v-model="artist.name_en"
                        v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_en)"
                        class="form-control"
                        type="text"
                        :disabled="artist.id && artistType !== 2"
                        @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                      <p
                        v-if="errors[`main_artists.${index}.name_en`]"
                        class="error-text">
                        {{ errors[`main_artists.${index}.name_en`][0] }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="vocaloid-image-input">
                  <div class="vocaloid-image-container">
                    <img
                      :src="`/images/${artist.image_path}`" />
                  </div>
                  <div class="vocaloid-input">
                    <div
                      :class="{ 'not-first-space': index === 0 }"
                      class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_ja_${index}`">ボーカロイド名
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_ja_${index}`"
                          v-model="artist.name_ja"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_ja`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_ja`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_kana_${index}`">ボーカロイド名（カナ表記）
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_kana_${index}`"
                          v-model="artist.name_kana"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_kana`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_kana`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_en_${index}`">ボーカロイド名（アルファベット表記)
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_en_${index}`"
                          v-model="artist.name_en"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_en`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_en`][0] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="!(type === 'single' && artistType !== 2)"
                  class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                  <button
                    v-if="item.main_artists.length - 1 === index
                      && item.main_artists.length < maxMainArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="onMainArtistAddClicked">
                    追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.main_artists.length - 1 === index
                      && item.main_artists.length < maxMainArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showVocaloidListPopUp()">
                    ボーカロイド/その他音声合成ソフトを追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="isShowMainArtistDeleteButton(artist.id)"
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    style="margin-bottom: 10px;clear-after: both"
                    @click.prevent="artist.type === 3? onVocaloidDeleteClicked(index,artist)
                      :onMainArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="item.main_artists.length === 0"
                class="form-group-wrap__btn-wrap  form-group-wrap__vocalo-btn-wrap">
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onMainArtistAddClicked">
                  追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="showVocaloidListPopUp()">
                  ボーカロイド/その他音声合成ソフトを追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">
                Featuringアーティスト
                <span>存在する場合、1名ずつ [追加] で登録します。
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artists)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.featuring_artists)"
                    class="sp-tooltip-icon">
                  </i>
                </span>
              </div>
              <div
                v-for="(artist, index) in item.featuring_artists"
                :key="index">
                <div v-if="artist.type !==3">
                  <div
                    :class="{ 'not-first-space': index === 0 }"
                    class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_ja_${index}`">アーティスト名
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_ja)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_ja_${index}`"
                        v-model="artist.name_ja"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_ja)"
                        class="form-control"
                        type="text"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_ja`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_ja`][0] }}
                      </p>
                      <p
                        v-else-if="featArtistWarnErrors[`featuring_artists.${index}.name_ja`]"
                        class="error-text">
                        {{ featArtistWarnErrors[`featuring_artists.${index}.name_ja`] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_kana_${index}`">アーティスト名（カナ表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_kana)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_kana_${index}`"
                        v-model="artist.name_kana"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_kana)"
                        class="form-control"
                        type="text"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_kana`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_kana`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_kana`][0] }}
                      </p>
                    </div>
                  </div>
                  <div class="form-group form-group--set">
                    <div class="form-label">
                      <label
                        class="control-label"
                        :for="`featuring_artist_name_en_${index}`">アーティスト名（アルファベット表記）
                        <span class="req">＊</span>
                      </label>
                      <i
                        v-tooltip="spTooltipConfig(tooltipMessages.featuring_artist_name_en)"
                        class="sp-tooltip-icon">
                      </i>
                    </div>
                    <div class="form-item">
                      <input
                        :id="`featuring_artist_name_en_${index}`"
                        v-model="artist.name_en"
                        v-tooltip="pcTooltipConfig(tooltipMessages.featuring_artist_name_en)"
                        class="form-control"
                        type="text"
                        @blur="onFieldChanged(`featuring_artists.${index}.name_en`)">
                      <p
                        v-if="errors[`featuring_artists.${index}.name_en`]"
                        class="error-text">
                        {{ errors[`featuring_artists.${index}.name_en`][0] }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="vocaloid-image-input">
                  <div class="vocaloid-image-container">
                    <img
                      :src="`/images/${artist.image_path}`" />
                  </div>
                  <div class="vocaloid-input">
                    <div
                      :class="{ 'not-first-space': index === 0 }"
                      class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_ja_${index}`">ボーカロイド名
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_ja_${index}`"
                          v-model="artist.name_ja"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_ja)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_ja`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_ja`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_ja`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_kana_${index}`">ボーカロイド名（カナ表記）
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_kana_${index}`"
                          v-model="artist.name_kana"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_kana)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_kana`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_kana`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_kana`][0] }}
                        </p>
                      </div>
                    </div>
                    <div class="form-group form-group--set">
                      <div class="form-label">
                        <label
                          class="control-label"
                          :for="`main_artist_name_en_${index}`">ボーカロイド名（アルファベット表記)
                          <span class="req">＊</span>
                        </label>
                        <i
                          v-tooltip="spTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="sp-tooltip-icon">
                        </i>
                      </div>
                      <div class="form-item">
                        <input
                          :id="`main_artist_name_en_${index}`"
                          v-model="artist.name_en"
                          v-tooltip="pcTooltipConfig(tooltipMessages.main_artist_name_en)"
                          class="form-control"
                          type="text"
                          :disabled="artist.id"
                          @blur="onFieldChanged(`main_artists.${index}.name_en`)">
                        <p
                          v-if="errors[`main_artists.${index}.name_en`]"
                          class="error-text">
                          {{ errors[`main_artists.${index}.name_en`][0] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                  <button
                    v-if="item.featuring_artists.length - 1 === index
                      && item.featuring_artists.length < maxFeaturingArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="onFeaturingArtistAddClicked">
                    追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.featuring_artists.length - 1 === index
                      && item.featuring_artists.length < maxFeaturingArtists"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="showVocaloidListPopUp(true)">
                    ボーカロイド/その他音声合成ソフトを追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="item.featuring_artists.length > 0"
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    @click.prevent="artist.type === 3 ? onFeatureVocaloidDeleteClicked(index,artist)
                      :onFeaturingArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="item.featuring_artists.length === 0"
                class="form-group-wrap__btn-wrap form-group-wrap__vocalo-btn-wrap">
                <button
                  class="c-btn c-btn--narrow"
                  type="button"
                  @click.prevent="onFeaturingArtistAddClicked">
                  追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="showVocaloidListPopUp(true)">
                  ボーカロイド/その他音声合成ソフトを追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label class="control-label">ボーカル入り
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.vocal)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.vocal)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="with_vocal_lyric">
                      <input
                        id="with_vocal_lyric"
                        v-model="item.vocal"
                        type="radio"
                        name="vocal"
                        :value="1"
                        checked
                        @change="onChangeWithVocal">
                      <div class="option-input"></div>
                      はい
                    </label>
                  </div>
                  <div class="radio">
                    <label for="without_vocal_music">
                      <input
                        id="without_vocal_music"
                        v-model="item.vocal"
                        type="radio"
                        name="vocal"
                        :value="0"
                        @change="onChangeWithOutVocal">
                      <div class="option-input"></div>
                      いいえ（インストゥルメンタル）
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="item.vocal === 1"
              class="form-group">
              <div class="form-label">
                <label class="control-label">歌詞・タイトルの表現について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.parental_warning)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.parental_warning)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-text">
                  Fワード、Nワードなどが、歌詞・タイトルに含まれる場合は「はい」を選択してください。<br />
                  また、ジャケット画像に [Parental Advisory] の記載がある場合、リリース内の1曲以上に“はい“を選択してください。
                </div>
                <div class="form-item-inline">
                  <span class="radio">
                    <label for="parental_warning1">
                      <input
                        id="parental_warning1"
                        v-model="item.parental_warning"
                        type="radio"
                        name="parental_warning"
                        value="explicit">
                      <div class="option-input"></div> はい
                    </label>
                  </span>
                  <span class="radio">
                    <label for="parental_warning0">
                      <input
                        id="parental_warning0"
                        v-model="item.parental_warning"
                        type="radio"
                        name="parental_warning"
                        value="no-explicit">
                      <div class="option-input"></div> いいえ
                    </label>
                  </span>
                </div>
                <p
                  v-if="errors.parental_warning"
                  class="error-text">
                  {{ errors.parental_warning[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.vocal === 1"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="language">歌唱言語
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.languages)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.languages)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="c-select_wrap">
                    <select
                      id="language"
                      v-model="item.language"
                      class="form-control c-select"
                      name="language">
                      <option
                        value=""
                        disabled
                        selected
                        style="display:none;">
                        選択してください
                      </option>
                      <option
                        v-for="(code, key) in languages"
                        :key="key"
                        :value="key">
                        {{ code }}
                      </option>
                    </select>
                  </div>
                  <span class="form-item-text--inline">歌詞に最も使用される言語を選択します。</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_group">著作権管理団体
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.copyright_group)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.copyright_group)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="c-select_wrap">
                    <select
                      id="copyright_group"
                      v-model="item.copyright_group"
                      class="form-control c-select"
                      name="language">
                      <option
                        value=""
                        disabled
                        selected
                        style="display:none;">
                        選択してください
                      </option>
                      <option
                        v-for="(code, key) in copyrightGroups"
                        :key="key"
                        :value="key">
                        {{ code }}
                      </option>
                    </select>
                  </div>
                  <p
                    v-if="errors.copyright_group"
                    class="error-text">
                    {{ errors.copyright_group[0] }}
                  </p>
                </div>
              </div>
            </div>
            <div class="form-group-wrap">
              <div class="form-group-wrap__title">
                著作者公表名
                <span>複数存在する場合、1名ずつ [追加] で登録します。
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.copyright_artists)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.copyright_artists)"
                    class="sp-tooltip-icon">
                  </i>
                  <p class="considerations-text c-color-caution">
                    ※作曲は必ず１つは入力してください。
                  </p>
                  <p
                    v-if="item.vocal === 1"
                    class="considerations-text c-color-caution">
                    ※ボーカル入り「はい」を選択した場合は作詞を必ず１つ入力してください。
                  </p>
                </span>
              </div>
              <div
                v-for="(artist, index) in item.copyright_artists"
                :key="index">
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label class="control-label">識別
                    </label>
                  </div>
                  <div class="form-item-inline">
                    <div class="checkbox">
                      <label :for="`copyright_type_composer_${index}`">
                        <input
                          :id="`copyright_type_composer_${index}`"
                          v-model="artist.roles"
                          value="composer"
                          type="checkbox"
                          :disabled="index === 0">
                        <div
                          class="option-input"
                          :class="{ 'required-composr': index === 0 }">
                        </div>
                        作曲
                      </label>
                    </div>
                    <div
                      v-if="item.vocal === 1"
                      class="checkbox">
                      <label :for="`copyright_type_lyricist_${index}`">
                        <input
                          :id="`copyright_type_lyricist_${index}`"
                          v-model="artist.roles"
                          value="lyricist"
                          type="checkbox">
                        <div class="option-input"></div>
                        作詞
                      </label>
                    </div>
                    <div class="checkbox">
                      <label :for="`copyright_type_arranger_${index}`">
                        <input
                          :id="`copyright_type_arranger_${index}`"
                          v-model="artist.roles"
                          value="arranger"
                          type="checkbox">
                        <div class="option-input"></div>
                        編曲
                      </label>
                    </div>
                    <p
                      v-if="errors[`copyright_artists.${index}.roles`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.roles`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label
                      class="control-label"
                      :for="`copyright_artist_name_ja_${index}`">著作者公表名
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.copyright_artist_name_ja)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      :id="`copyright_artist_name_ja_${index}`"
                      v-model="artist.name_ja"
                      v-tooltip="pcTooltipConfig(tooltipMessages.copyright_artist_name_ja)"
                      class="form-control"
                      type="text"
                      @blur="onFieldChanged(`copyright_artists.${index}.name_ja`)">
                    {{ errors.copyright_artists }}
                    <p
                      v-if="errors[`copyright_artists.${index}.name_ja`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.name_ja`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group form-group--set">
                  <div class="form-label">
                    <label
                      class="control-label"
                      :for="`copyright_artist_name_en_${index}`">著作者公表名 （アルファベット表記）
                    </label>
                    <i
                      v-tooltip="spTooltipConfig(tooltipMessages.copyright_artist_name_en)"
                      class="sp-tooltip-icon">
                    </i>
                  </div>
                  <div class="form-item">
                    <input
                      :id="`copyright_artist_name_en_${index}`"
                      v-model="artist.name_en"
                      v-tooltip="pcTooltipConfig(tooltipMessages.copyright_artist_name_en)"
                      class="form-control"
                      type="text"
                      @blur="onFieldChanged(`copyright_artists.${index}.name_en`)">
                    <p
                      v-if="errors[`copyright_artists.${index}.name_en`]"
                      class="error-text">
                      {{ errors[`copyright_artists.${index}.name_en`][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group-wrap__btn-wrap">
                  <button
                    v-if="item.copyright_artists.length - 1 === index"
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="onCopyrightArtistAddClicked">
                    追加
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <button
                    v-if="index > 0 && item.copyright_artists.length > 1"
                    type="button"
                    class="c-btn c-btn--delete-large c-btn--narrow c-btn--right"
                    @click.prevent="onCopyrightArtistDeleteClicked(index)">
                    削除
                  </button>
                </div>
              </div>
              <div
                v-if="item.copyright_artists.length === 0"
                class="form-group-wrap__btn-wrap">
                <button
                  class="c-btn c-btn--narrow"
                  type="button"
                  @click.prevent="onCopyrightArtistAddClicked">
                  追加
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
            </div>
            <div
              v-if="isBtoB && type === 'album'"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_p">著作権表記(C)
                </label>
              </div>
              <div class="form-item">
                <input
                  id="copyright_c"
                  v-model="item.copyright_c_copyright"
                  class="form-control copyright-form-control"
                  type="text"
                  placeholder="XXXXX Records"
                  @blur="onFieldChanged('copyright_c_copyright')">
                <p
                  v-if="errors.copyright_c_copyright"
                  class="error-text">
                  {{ errors.copyright_c_copyright[0] }}
                </p>
              </div>
              <div class="form-item-inline form-item-inline--year">
                <input
                  id="copyright_c_year"
                  v-model="item.copyright_c_year"
                  class="form-control copyright-year-form-control"
                  type="text"
                  maxlength="4"
                  placeholder="2020"
                  @blur="onFieldChanged('copyright_c_year')">
                <p
                  v-if="errors.copyright_c_year"
                  class="error-text">
                  {{ errors.copyright_c_year[0] }}
                </p>
              </div>
            </div>

            <div
              v-if="isBtoB && type === 'album'"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="copyright_p">原盤権表記(P)
                </label>
              </div>
              <div class="form-item">
                <input
                  id="copyright_p"
                  v-model="item.copyright_p_copyright"
                  class="form-control copyright-form-control"
                  type="text"
                  placeholder="XXXXX Records"
                  @blur="onFieldChanged('copyright_p_copyright')">
                <p
                  v-if="errors.copyright_p_copyright"
                  class="error-text">
                  {{ errors.copyright_p_copyright[0] }}
                </p>
              </div>
              <div class="form-item-inline form-item-inline--year">
                <input
                  id="copyright_p_year"
                  v-model="item.copyright_p_year"
                  class="form-control copyright-year-form-control"
                  type="text"
                  maxlength="4"
                  placeholder="2020"
                  @blur="onFieldChanged('copyright_p_year')">
                <p
                  v-if="errors.copyright_p_year"
                  class="error-text">
                  {{ errors.copyright_p_year[0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="isrc">ISRC
                  <i
                    v-tooltip="pcTooltipConfig(
                      isBtoC ? tooltipMessages.isrc : tooltipMessages.isrc_tob,
                      true)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.isrc)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <input
                  id="isrc"
                  v-model="item.isrc"
                  class="form-control"
                  type="text">
                <p
                  v-if="errors.isrc"
                  class="error-text">
                  {{ errors.isrc[0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="listening_start_position">試聴開始位置
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.listening_position)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.listening_position)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item-inline">
                <div class="form-item">
                  <div class="form-item-inline form-item-inline--sp-reset">
                    <span class="form-item__wrap">
                      <input
                        id="listening_start_position"
                        v-model="item.trial_start_min"
                        class="form-control form-control--time"
                        type="text"
                        maxlength="2">分
                    </span>
                    <span class="form-item__wrap">
                      <input
                        id="listening_start_position_second"
                        v-model="item.trial_start_sec"
                        class="form-control form-control--time"
                        type="text"
                        maxlength="2">秒
                    </span>
                  </div>
                  <p
                    v-if="errors.trial_start_min"
                    class="error-text">
                    {{ errors.trial_start_min[0] }}
                  </p>
                  <p
                    v-if="errors.trial_start_sec"
                    class="error-text">
                    {{ errors.trial_start_sec[0] }}
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="isBtoC"
              class="form-group">
              <div class="form-label">
                <label class="control-label">生成AIの利用について
                  <span class="req">＊</span>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="is_ai_use">
                      <input
                        id="is_ai_use"
                        v-model="item.is_ai_use"
                        type="radio"
                        name="ai_use"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_ai_use">
                      <input
                        id="not_ai_use"
                        v-model="item.is_ai_use"
                        type="radio"
                        name="ai_use"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div
                  v-if="item.is_ai_use"
                  class="form-item-text">
                  「使用している」を選択した場合、権利物の種類に「生成AIによって制作された音源」を追加する必要があります
                </div>
                <p
                  v-if="errors.is_ai_use"
                  class="error-text">
                  {{ errors.is_ai_use[0] }}
                </p>
              </div>
            </div>
            <rights-documents
              v-if="isBtoC"
              v-model="rightsDocuments"
              :use-rights="item.use_rights"
              :errors="errors"
              @update-rights-check="checkUseRights" />
            <div
              v-if="item.classification===1"
              class="form-group">
              <div class="form-label">
                <label class="control-label">フリートラックの使用について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.freetrack)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.freetrack)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="use_freetrack">
                      <input
                        id="use_freetrack"
                        v-model="item.freetrack"
                        type="radio"
                        name="freetrack"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_use_freetrack">
                      <input
                        id="not_use_freetrack"
                        v-model="item.freetrack"
                        type="radio"
                        name="freetrack"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div class="form-item-text">
                  <router-link
                    v-if="false"
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 143 : 0} }"
                    class="c-text-link"
                    target="_blank">
                    フリートラックとは
                  </router-link>
                </div>
                <p
                  v-if="errors.freetrack"
                  class="error-text">
                  {{ errors.freetrack[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.classification===1 && isBtoC"
              class="form-group">
              <div class="form-label">
                <label class="control-label">著作権フリー楽曲の使用について
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.use_copyright_free)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.use_copyright_free)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="use_copyright_free">
                      <input
                        id="use_copyright_free"
                        v-model="item.useCopyrightFreek"
                        type="radio"
                        name="useCopyrightFreek"
                        :value="1">
                      <div class="option-input"></div>
                      使用している
                    </label>
                  </div>
                  <div class="radio">
                    <label for="not_use_copyright_free">
                      <input
                        id="not_use_copyright_free"
                        v-model="item.useCopyrightFreek"
                        type="radio"
                        name="useCopyrightFreek"
                        :value="0">
                      <div class="option-input"></div>
                      使用していない
                    </label>
                  </div>
                </div>
                <div class="form-item-text">
                  <router-link
                    v-if="false"
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 144 : 0} }"
                    class="c-text-link"
                    target="_blank">
                    著作権フリーとは
                  </router-link>
                </div>
                <p
                  v-if="errors.useCopyrightFreek"
                  class="error-text">
                  {{ errors.useCopyrightFreek[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="item.classification===1 && isBtoC"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  style="white-space: normal;padding-right:20px;">著作権フリー楽曲として販売していた実績がある
                  <span class="req">＊</span>
                  <i
                    v-tooltip="pcTooltipConfig(tooltipMessages.has_sold_copyright_free)"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig(tooltipMessages.has_sold_copyright_free)"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="has_sold_copyright_free">
                      <input
                        id="has_sold_copyright_free"
                        v-model="item.hasSoldCopyrightFree"
                        type="radio"
                        name="hasSoldCopyrightFree"
                        :value="1">
                      <div class="option-input"></div>
                      はい
                    </label>
                  </div>
                  <div class="radio">
                    <label for="has_not_sold_copyright_free">
                      <input
                        id="has_not_sold_copyright_free"
                        v-model="item.hasSoldCopyrightFree"
                        type="radio"
                        name="hasSoldCopyrightFree"
                        :value="0">
                      <div class="option-input"></div>
                      いいえ
                    </label>
                  </div>
                </div>
                <p
                  v-if="errors.hasSoldCopyrightFree"
                  class="error-text">
                  {{ errors.hasSoldCopyrightFree[0] }}
                </p>
              </div>
            </div>

            <div
              v-if="!disableContentIdUser && item.classification===1"
              class="form-group"
              style="border: none;"
              :class="{ disabled: isSpokenWord || disableContentId}">
              <div class="form-label">
                <label class="control-label">YouTubeマネタイズサービス
                  <span class="req">＊</span>
                  <i
                    v-tooltip="'YouTubeマネタイズサービスを利用すると自動でYoutube Musicにも配信されます。'"
                    class="tooltip-icon">
                  </i>
                  <i
                    v-tooltip="spTooltipConfig('YouTubeマネタイズサービスを利用すると自動でYoutube Musicにも配信されます。')"
                    class="sp-tooltip-icon">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline">
                  <div class="radio">
                    <label for="with_content_id">
                      <input
                        id="with_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        class="form-control"
                        :disabled="isSpokenWord || disableContentId"
                        :value="1"
                        @change="onFieldChanged('_restrictions_0_invalid_youtube_monetize')">
                      <div class="option-input"></div>
                      利用する
                    </label>
                  </div>
                  <div class="radio">
                    <label for="without_content_id">
                      <input
                        id="without_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        class="form-control"
                        :disabled="isSpokenWord || disableContentId"
                        :value="0"
                        @change="onFieldChanged('_restrictions_0_invalid_youtube_monetize')">
                      <div class="option-input"></div>
                      利用しない
                    </label>
                  </div>
                  <div
                    v-if="false"
                    class="radio">
                    <label for="both_content_id">
                      <input
                        id="both_content_id"
                        v-model="enableContentId"
                        type="radio"
                        name="contentsID"
                        :value="2">
                      <div class="option-input"></div>
                      配信 + 利用
                    </label>
                  </div>
                </div>
                <div v-if="isSpokenWord">
                  <span style="font-size: 14px; color: red;">
                    ジャンルに「Spoken Word」が設定されているので、YouTubeマネタイズサービスは利用出来ません。
                  </span>
                </div>
                <div
                  v-if="isBtoB"
                  class="form-item-text">
                  <router-link
                    :to="{ name : 'faq-detail', params: {id: isBtoC ? 58 : 33} }"
                    class="c-text-link"
                    target="_blank">
                    YouTubeマネタイズサービスとは？
                  </router-link>
                </div>
              </div>
            </div>
            <div
              v-if="isBtoC && !disableContentIdUser && item.classification===1"
              style="margin-top: -20px"
              class="form-group"
              :class="{ disabled: isSpokenWord || disableContentId}">
              <div class="form-label"></div>
              <div class="form-item">
                <p
                  style="margin-left: 0!important;"
                  class="considerations-text c-color-caution">
                  YouTubeマネタイズサービスの内容と注意事項をお読みいただき下記の同意をお願いします。<br />
                  ※最後までスクロールしていただかないと、同意のチェックができません。
                </p>
                <div
                  style="text-align: center"
                  :class="{ 'apple': isAppleDevice }">
                  <div
                    class="agree-area"
                    @scroll="onScrollAgreement">
                    YouTubeマネタイズサービス
                    <span class="asterisk">*1</span>
                    とは、第三者がYouTubeにアップロードする動画にあなたの楽曲が使用されると、
                    音源の権利として収益を受け取ることができるサービスです。ぜひご活用ください。
                    <br />
                    <a
                      href="https://support.google.com/youtube/answer/2797370?hl=ja"
                      class="c-text-link"
                      target="_blank">
                      詳しくはこちら。
                    </a>
                    <br /><br /><br />
                    ただし、当サービスから配信している楽曲を使用した動画をYouTubeへアップする場合、
                    YouTube上で「著作権侵害の申し立て」と判断される可能性があります。<br />
                    その際、著作権者として 「massenext.Inc(narasuの代理会社)」
                    <span class="asterisk">*2</span>
                    と記載されます。<br />
                    ただしこの場合の「著作権」とは「原盤権（音源に関する権利）」のことを指しており、
                    「作曲や作詞に関する著作権」のことではありませんのでご注意ください。<br />
                    著作権侵害の申し立ての取り下げをご希望の方は、
                    <a
                      href="/client/contact/"
                      class="c-text-link"
                      target="_blank">
                      お問い合わせフォーム
                    </a>
                    より下記を明記のうえお問い合わせください。<br /><br />
                    ●著作権侵害の申し立てに関して<br />
                    &nbsp;・申し立てを受けた動画URL:<br />
                    &nbsp;・対象楽曲名:<br />
                    &nbsp;・対象リリースのJANコード:<br />
                    <br /><br />
                    「YouTubeマネタイズサービス」の登録対象外の楽曲は以下となります。<br />
                    <ul>
                      <li class="list">パプリックドメインに帰した知的創作物を含む楽曲（クラシック楽曲の音源など）</li>
                      <li class="list">第三者が権利を有する、またはライセンスしている音源を含む楽曲（サンプリング音源・ライセンス音源など）</li>
                      <li class="list">フリー素材の音源を含む楽曲（音楽制作ソフトのループ音源素材・サウンドエフェクト音源素材など）</li>
                      <li class="list">オリジナル楽曲と差異のないカバー楽曲（有名アーティストの歌真似など）</li>
                      <li class="list">オリジナル楽曲のカラオケ、アカペラ、インスト、ショート、リマスター、ライブバージョンなどの楽曲</li>
                      <li class="list">オリジナル盤以外のアルバム（コンピレーション盤・リマスター盤・ベスト盤・DJミックス盤など）</li>
                      <li class="list">30秒未満の楽曲</li>
                      <li class="list">音楽以外のポッドキャスト、スピーチ、インタビュー、朗読などのコンテンツ</li>
                      <li class="list">音の識別が難しい楽曲(自然環境音、アンビエント、ヨガ、スリープに部類される楽曲など)</li>
                      <li class="list">当社以外のサービスから YouTube『YouTubeマネタイズサービス』へ登録された楽曲</li>
                    </ul>
                    ※『YouTubeマネタイズサービス』へのご登録後、当社以外からのContent ID登録が確認できた場合、ご連絡を差し上げずに登録を解除いたします。
                    <br />
                    <br />
                    <span class="asterisk">*1</span><br />
                    「YouTubeマネタイズサービス」を利用すると、自動でYouTube Musicにも配信されます<br /><br />
                    <span class="asterisk">*2</span><br />
                    narasuでは、YouTube(google)と音楽契約をもつ株式会社massenextと業務提携しており、
                    「YouTubeマネタイズサービス」に関しては、音楽エージェントサイトZULAを運営する株式会社massenextを通じて運用管理をしております。<br />
                    YouTube上では「著作権」という表記の中に「原盤権」も含まれており、ここでの「著作権」とは「原盤権」のことを指します。<br />
                    <br />
                  </div>
                </div>
                <div class="consent-checkbox-area">
                  <div class="checkbox">
                    <label>
                      <input
                        v-model="item.youtube_monetize_agreed"
                        :value="1"
                        :disabled="!readAgreement || alreadyAgreed || disableContentId"
                        type="checkbox">
                      <div
                        class="option-input"
                        :style="{ 'opacity': readAgreement ? '100%' : '30%' }">
                      </div>
                      YouTubeマネタイズサービスの内容と注意事項を理解・同意しました
                    </label>
                  </div>
                </div>
                <p
                  v-if="errors._restrictions_0_invalid_youtube_monetize"
                  class="error-text">
                  {{ errors._restrictions_0_invalid_youtube_monetize[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="false"
              class="form-group">
              <div class="form-label form-label--top">
                <label
                  class="control-label"
                  for="song_introduction_domestic">楽曲紹介文
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.remarks_ja)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <textarea
                  id="song_introduction_domestic"
                  v-model="item.remarks_ja"
                  v-tooltip="pcTooltipConfig(tooltipMessages.remarks_ja)"
                  class="c-textarea">
                </textarea>
                <p
                  v-if="errors.remarks_ja"
                  class="error-text">
                  {{ errors.remarks_ja[0] }}
                </p>
              </div>
            </div>
            <div
              v-if="false"
              class="form-group">
              <div class="form-label form-label--top">
                <label
                  class="control-label"
                  for="song_introduction_overseas">
                  楽曲紹介文（アルファベット表記）
                </label>
                <i
                  v-tooltip="spTooltipConfig(tooltipMessages.remarks_en)"
                  class="sp-tooltip-icon">
                </i>
              </div>
              <div class="form-item">
                <textarea
                  id="song_introduction_overseas"
                  v-model="item.remarks_en"
                  v-tooltip="pcTooltipConfig(tooltipMessages.remarks_en)"
                  class="c-textarea">
                </textarea>
                <p
                  v-if="errors.remarks_en"
                  class="error-text">
                  {{ errors.remarks_en[0] }}
                </p>
              </div>
            </div>
            <VocaloidContest
              :bus="bus"
              :track="item"
              :product-id="productId" />
          </div>
        </form>
        <div class="c-btn-wrap">
          <a
            v-if="isBtoC && !disableContentId && enableContentId && !item.youtube_monetize_agreed"
            class="c-btn c-btn--back">
            登録する
            <span class="c-btn__icon-arrow"></span>
          </a>
          <a
            v-else
            class="c-btn"
            href="#"
            @click.prevent="onRegisterClicked()">
            登録する
            <span class="c-btn__icon-arrow"></span>
          </a>
          <a
            class="c-btn c-btn--back c-btn--back-left"
            href="#"
            @click.prevent="onReturnClicked">
            戻る
            <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          </a>
        </div>
      </div>
    </div>
    <release-check-list />
    <basic-modal
      v-if="popUpDialogShown"
      :is-active="popUpDialogShown"
      :remaining-number="isFeaturing ?
        100-(item.main_artists.length + selectedFeatureVocaloids.length)
        : ((artistType == 2)? 100: 4)-(item.main_artists.length)"
      :already-selected-vocaloid="isFeaturing ? selectedFeatureVocaloids : selectedVocaloids"
      :comparable-vocaloid="isFeaturing ? selectedVocaloids : selectedFeatureVocaloids "
      :is-adding-for-featuring="isFeaturing"
      :is-selected-user-artist="isSelectedUserArtist"
      width="700px"
      title="ボーカロイド"
      @item-to-be-deleted="itemToBeDeleted($event)"
      @modalConfirm="modalConfirm($event)"
      @modal-cancel="closeModel">
    </basic-modal>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import ReleaseStepArea from '../StepArea'
import ReleaseCheckList from '../CheckList'
import HeaderBar from '../../HeaderBar'
import ReleaseTypeLabel from '../ReleaseTypeLabel'
import AlertView from '../../AlertView'
import tooltip from '../../../mixins/tooltip'
import tooltipMessages from './track-tooltips'
import BasicModal from '../../../../components/Modal'
import VocaloidContest from '../../Campaign/VocaloidContest'
import RightsDocuments from './RightsDocuments'

let vTimer = null
export default {
  name: 'ReleaseStep2Track',
  components: {
    ReleaseTypeLabel,
    HeaderBar,
    ReleaseCheckList,
    ReleaseStepArea,
    AlertView,
    BasicModal,
    VocaloidContest,
    RightsDocuments,
  },
  mixins: [
    tooltip,
  ],
  props: {
    tooltipMessages: {
      type: Object,
      default: () => tooltipMessages,
    },
    maxFeaturingArtists: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      type: '',
      artistType: 2,
      artists: [],
      discIndex: null,
      item: {
        title_ja: '',
        title_kana: '',
        title_en: '',
        version_ja: '',
        version_kana: '',
        version_en: '',
        main_artists: [],
        featuring_artists: [],
        vocal: 1,
        freetrack: null,
        useCopyrightFreek: null,
        hasSoldCopyrightFree: null,
        parental_warning: 'no-explicit',
        language: 'ja',
        lyrics: '',
        copyright_group: '7',
        copyright_artists: [],
        isrc: '',
        trial_start_min: 0,
        trial_start_sec: 0,
        remarks_ja: '',
        remarks_en: '',
        is_omnibus: 0,
        is_ai_use: 0,
        use_rights: 0,
        copyright_p_copyright: '',
        copyright_p_year: '',
        copyright_c_copyright: '',
        copyright_c_year: '',
        classification: 1,
        permission_cover_music: null,
        contents_id_enabled: 0,
        duration: null,
        youtube_monetize_agreed: false,
        _restrictions: [],
        vocaloid: [],
      },
      errors: {},
      languages: [],
      copyrightGroups: [],
      version_status: false,
      realtimeValidation: true,
      enableContentId: false,
      hideVersion: false,
      hasInitialDigit: false,
      versionAddedCover: false,
      releaseArtistsCount: 1,
      showYouTubeMonetizeDialog: false,
      readAgreement: false,
      alreadyAgreed: false,
      maxMainArtists: 4,
      isSpokenWord: false,
      popUpDialogShown: false,
      selectedVocaloids: [],
      selectedFeatureVocaloids: [],
      isFeaturing: false,
      productId: 0,
      bus: new Vue(),
      foundVersioWords: false,
      isSelectedUserArtist: false,
      albumArtistIds: [],
      mainArtistWarnErrors: [],
      featArtistWarnErrors: [],
      rightsDocuments: [],
    }
  },
  computed: {
    disableContentId() {
      let disabled = this.item.classification !== 1
                    || parseInt(this.item.freetrack, 10) !== 0
      if (this.isBtoC) {
        disabled = disabled
                || parseInt(this.item.useCopyrightFreek, 10) !== 0
                || parseInt(this.item.hasSoldCopyrightFree, 10) !== 0
                || parseInt(this.item.use_rights, 10) !== 0
      }
      return !this.item.duration
        ? disabled
        : this.durationToSeconds(this.item.duration) <= 29 || disabled
    },
    disableContentIdUser() {
      return !this.$store.state.auth.enable_content_id
    },
    disableTitleChange() {
      return this.type === 'single'
    },
    isAppleDevice() {
      return window.navigator.userAgent.indexOf('iPhone') !== -1
        || window.navigator.userAgent.indexOf('iPad') !== -1
        || window.navigator.userAgent.indexOf('Mac') !== -1
    },
  },
  watch: {
    enableContentId(value) {
      const restriction = this.getYoutubeRestriction()
      if (!restriction) return
      if (value === false) {
        this.item.contents_id_enabled = false
        restriction.settings.contents_id_enabled = 0
      } else {
        this.item.contents_id_enabled = value > 0 ? 1 : 0
        restriction.settings.contents_id_enabled = value > 0 ? 1 : 0
      }
    },
    'item.freetrack': {
      handler(value) {
        if (value !== 0) {
          // YouTubeマネタイズサービス を未選択の状態にする
          this.enableContentId = false
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.freetrack = value
      },
    },
    'item.use_rights': {
      handler(value) {
        if (value !== 0) {
          // YouTubeマネタイズサービス を未選択の状態にする
          this.enableContentId = false
        }
      },
    },
    'item.useCopyrightFreek': {
      handler(value) {
        if (value !== 0) {
          // YouTubeマネタイズサービス を未選択の状態にする
          this.enableContentId = false
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.use_copyright_freek = value
      },
    },
    'item.hasSoldCopyrightFree': {
      handler(value) {
        if (value !== 0) {
          // YouTubeマネタイズサービス を未選択の状態にする
          this.enableContentId = false
        }
        const restriction = this.getYoutubeRestriction()
        if (!restriction) return
        restriction.settings.contents_id_confirm.has_sold_copyright_free = value
      },
    },
    errors: {
      handler(value) {
        document.querySelectorAll('.error-input').forEach(input => {
          input.classList.remove('error-input')
        })
        if (Object.keys(value).length) {
          setTimeout(() => {
            const errorText = document.querySelectorAll('.error-text')
            if (errorText) {
              errorText.forEach(text => {
                text.previousElementSibling.classList.add('error-input')
              })
            }
          }, 500)
        }
      },
      deep: true,
    },
  },
  mounted() {
    // セッションストレージから一時保存されたデータを読み込み
    const release = JSON.parse(window.sessionStorage.getItem('album'))
    const productId = release.product.id
    this.productId = release.product.id
    if (!release) {
      this.$router.push({ name: 'index' })
      return
    }
    this.type = release.type
    this.artistType = release.artistType
    // this.maxMainArtists = release.artistType === 2 ? Number.MAX_SAFE_INTEGER : 4
    this.maxMainArtists = Number.MAX_SAFE_INTEGER
    // Various Artist はオムニバスとして登録する
    this.item.is_omnibus = this.artistType === 2
    this.discIndex = this.$route.params.discIndex
    this.releaseArtistsCount = release.artists.length
    const { tracks } = release.discs[this.$route.params.discIndex]
    if (tracks[this.$route.params.trackIndex]) {
      this.item = Object.assign(this.item, tracks[this.$route.params.trackIndex])
      // restrictionsにYoutubeが設定されていたら、contentsIDの設定を取り出す
      const restriction = this.getYoutubeRestriction()
      if (restriction) {
        this.enableContentId = (parseInt(restriction.settings.contents_id_enabled, 10) || 0) === 1
          ? 1 : 0
        if (restriction.settings.contents_id_confirm) {
          /* eslint-disable max-len */
          this.item.freetrack = parseInt(restriction.settings.contents_id_confirm.freetrack, 10) || null
          this.item.useCopyrightFreek = parseInt(restriction.settings.contents_id_confirm.use_copyright_freek, 10) || null
          this.item.hasSoldCopyrightFree = parseInt(restriction.settings.contents_id_confirm.has_sold_copyright_free, 10) || null
          /* eslint-enable */
        } else {
          // 古い楽曲には設定されていないので追加する
          restriction.settings.contents_id_confirm = {
            freetrack: null,
            use_copyright_freek: null,
            has_sold_copyright_free: null,
          }
          this.item.useCopyrightFreek = null
          this.item.hasSoldCopyrightFree = null
        }
      }
      this.item.main_artists.some((artist, i) => {
        if (release.artists.indexOf(artist.id) === -1) {
          this.releaseArtistsCount = i
          return true
        }
        return false
      })
      // メインアーティストにVarious Artistsがあったらクリア
      for (let i = 0; i < this.item.main_artists.length; i += 1) {
        if (this.item.main_artists[i].type === 2) {
          this.$set(this.item.main_artists, i, {
            name_ja: '', name_kana: '', name_en: '', type: 1,
          })
        }
      }
      this.albumArtistIds = release.artists
    } else {
      // 一時保存されたデータがなければアーティスト情報を読み込み、メインアーティストをデフォルト値としてセットする
      this.fetchProductArtists(productId).then(() => {
        this.item.main_artists = this.artists
        this.item.main_artists.forEach(artist => {
          this.albumArtistIds.push(artist.id)
          if (artist.type === 3) {
            this.selectedVocaloids.push(artist)
          }
        })
        if (this.item.featuring_artists.length > 0) {
          this.item.featuring_artists.forEach(vocloid => {
            if (vocloid.type === 3) {
              this.selectedFeatureVocaloids.push(vocloid)
            }
          })
        }
      })
    }

    this.item.main_artists.forEach(vocaloid1 => {
      if (vocaloid1.type === 3) {
        this.selectedVocaloids.push(vocaloid1)
      }
    })
    if (this.item.featuring_artists.length > 0) {
      this.item.featuring_artists.forEach(vocloid => {
        if (vocloid.type === 3) {
          this.selectedFeatureVocaloids.push(vocloid)
        }
      })
    }
    if (release.type === 'single'
      && tracks[this.$route.params.trackIndex]?.copyright_artists.length === 0
      && release.artists.length > 0) {
      this.fetchArtistsByIds(release.artists)
    }
    if (tracks[this.$route.params.trackIndex]?.copyright_artists.length === 0) {
      const lVocaloid = release.vocaloid || []
      if (lVocaloid.length > 0) {
        release.vocaloid.forEach(vocaloid1 => {
          if (this.item.main_artists.filter(e => e.name_ja === vocaloid1.name_ja).length === 0) {
            this.item.main_artists.push(vocaloid1)
            this.selectedVocaloids.push(vocaloid1)
          }
        })
      }
    }
    this.item.main_artists = this.item.main_artists.filter(artist => artist.name_ja !== '')

    // 楽曲だけ登録して抜けてしまうと楽曲メインアーティストのIDがセットされていない不具合があったので
    // releaseArtistsCountが0の場合は登録されているアルバムメインアーティストで名寄せしてIDをセットする
    if (this.releaseArtistsCount === 0) {
      this.fetchProductArtists(productId).then(() => {
        for (let i = 0; i < release.artists.length; i += 1) {
          if (this.item.main_artists[i]) {
            const result = this.artists.find(
              data => data.name_ja === this.item.main_artists[i].name_ja
                && data.name_kana === this.item.main_artists[i].name_kana
                && data.name_en === this.item.main_artists[i].name_en,
            )
            if (result) {
              this.item.main_artists[i].id = result.id
              this.releaseArtistsCount += 1
            }
          } else {
            break
          }
        }
      })
    }

    // ない場合は仮のデータを作る
    if (!this.getYoutubeRestriction()) {
      // eslint-disable-next-line no-underscore-dangle
      this.item._restrictions.push({
        country: 'WW',
        provider_id: 12,
        start: moment(new Date()).format('YYYY-MM-DD'),
        provider: {
          id: 12,
          key: 'youtube',
        },
        settings: {
          contents_id_enabled: 1,
          contents_enabled: 1,
          contents_id_confirm: {
            freetrack: null,
            use_copyright_freek: null,
            has_sold_copyright_free: null,
          },
        },
      })
    }
    // toCでsingleの場合にタイトルを強制セットする
    if (this.type === 'single') {
      this.item.title_ja = release.product.title_ja ?? ''
      this.item.title_en = release.product.title_en ?? ''
      this.item.title_kana = release.product.title_kana ?? ''
      this.item.permission_cover_music = release.product.permission_cover_music ?? ''
      if (release.product.version_ja && this.item.version_ja === '') {
        this.item.version_ja = release.product.version_ja ?? ''
        this.item.version_en = release.product.version_en ?? ''
        this.item.version_kana = release.product.version_kana ?? ''
      }
    }
    if (this.isBtoC) {
      if (this.item.classification === 0) this.item.classification = 1
    }
    // 各種マスタ初期化
    this.fetchLanguages()
    this.fetchCopyrightGroups()
    // バージョンの情報取得したときに中身があるときの処理
    this.versionContentsCheck()
    // コピーライトアーティストの項目が存在しない時、作曲者を初期値で追加
    if (!this.item.copyright_artists[0]) {
      this.onCopyrightArtistAddClicked()
    }
    this.checkTitleInitialHasDigit()
    this.isSpokenWord = (!this.item.id || this.enableContentId === 0) && [release.product.genre1, release.product.genre2].some(genre => genre === 'Spoken Word')

    // YouTube マネタイズの同意
    this.alreadyAgreed = release.product.youtube_monetize_agreed
    this.item.youtube_monetize_agreed = release.product.youtube_monetize_agreed
    if (this.item.youtube_monetize_agreed) {
      this.readAgreement = true
    }
    // ジャンルにSpoken Wordが選択されていた場合は強制的に「利用しない」をセット
    if (this.isSpokenWord) {
      this.readAgreement = true
      this.item.youtube_monetize_agreed = true
      this.enableContentId = 0
    }
    if (this.$store.state.auth.permission === 10) {
      this.readAgreement = true
      this.item.youtube_monetize_agreed = true
    }
    if (this.item.id
        && (this.item.classification === 2 || this.item.classification === 3)) {
      this.item.permission_cover_music = 1
    }
    if (this.item.classification === 2) {
      this.version_status = true
      this.versionAddedCover = true
    }
    this.rightsDocuments = this.item.rights_documents ?? []
  },
  methods: {
    // 言語マスタを取得
    async fetchLanguages() {
      const { data } = await window.axios.get('/api/client/code/languages')
      this.languages = data
    },
    async fetchArtistsByIds(ids) {
      const { data } = await window.axios.get('/api/client/artists', {
        params: { ids },
      })
      data.data.forEach(singleArtist => {
        if (!this.item.main_artists.filter(artist => artist.name_ja
          === singleArtist.name_ja).length > 0) {
          // eslint-disable-next-line no-param-reassign
          // singleArtist.type = null
          const artist = singleArtist
          // メインアーティストにVarious Artistsがあったらクリア
          if (singleArtist.type === 2) {
            this.item.main_artists.push({
              name_ja: '', name_kana: '', name_en: '', type: 1,
            })
          } else {
            this.item.main_artists.push(artist)
          }
        }
      })
    },
    // 著作権団体マスタを取得
    async fetchCopyrightGroups() {
      const { data } = await window.axios.get('/api/client/code/copyright-groups')
      this.copyrightGroups = data
    },
    // アーティスト情報の取得と初期値のセット
    async fetchProductArtists(productId) {
      const { data } = await window.axios.get(`/api/client/products/${productId}/artists`)
      data.forEach(item => {
        if (!this.artists.filter(artist => artist.name_ja === item.name_ja).length > 0) {
          const artist = item
          // アーティストにVarious Artistsがあったらクリア
          if (artist.type === 2) {
            this.artists.push({
              name_ja: '', name_kana: '', name_en: '', type: 1,
            })
          } else {
            this.artists.push(artist)
          }
        }
      })
    },
    // 戻るボタンクリック
    onReturnClicked() {
      const { discIndex, trackIndex } = this.$route.params
      this.$router.push({
        name: `${this.$route.meta.sideMenuId}-step2`,
        hash: `#disc${discIndex}-track${trackIndex}`,
      })
    },
    onFieldChanged(key) {
      /* display warning message if the japanese and alphabet characters
      along with symbols are entered in japanese name input field */
      const regexMainArtists = /^main_artists\.\d+\.name_ja$/
      const regexFeaturingArtists = /^featuring_artists\.\d+\.name_ja$/
      /*
         Hiragana: [\u3040-\u309F]
         Katakana: [\u30A0-\u30FF]
         Kanji: [\u4E00-\u9FAF]
      */
      const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/
      const alphabetRegex = /[A-Za-z]/
      const specialCharactersRegex = /[()（）\\[\]【】<>＜＞『』]/

      if (regexMainArtists.test(key) && this.item.main_artists.length > 0) {
        this.item.main_artists.forEach((artist, idx) => {
          const containsJapanese = japaneseRegex.test(artist.name_ja)
          const containsAlphabet = alphabetRegex.test(artist.name_ja)
          const containsSpecialCharacters = specialCharactersRegex.test(artist.name_ja)
          const errorKey = `main_artists.${idx}.name_ja`

          if ((containsJapanese && containsAlphabet) || containsSpecialCharacters) {
            this.$set(this.mainArtistWarnErrors, errorKey, '山田 太郎（TARO YAMADA）など、翻訳表記を含めることはできません。')
          } else {
            this.$delete(this.mainArtistWarnErrors, errorKey)
          }
        })
      }

      if (regexFeaturingArtists.test(key) && this.item.featuring_artists.length > 0) {
        this.item.featuring_artists.forEach((artist, idx) => {
          const containsJapanese = japaneseRegex.test(artist.name_ja)
          const containsAlphabet = alphabetRegex.test(artist.name_ja)
          const containsSpecialCharacters = specialCharactersRegex.test(artist.name_ja)
          const errorKey = `featuring_artists.${idx}.name_ja`

          if ((containsJapanese && containsAlphabet) || containsSpecialCharacters) {
            this.$set(this.featArtistWarnErrors, errorKey, '山田 太郎（TARO YAMADA）など、翻訳表記を含めることはできません。')
          } else {
            this.$delete(this.featArtistWarnErrors, errorKey)
          }
        })
      }
      if (vTimer) {
        clearTimeout(vTimer)
      }
      vTimer = setTimeout(() => {
        this.onRegisterClicked(key)
      }, 500)
    },
    onScrollAgreement(event) {
      if (!this.readAgreement) {
        if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight - 20) {
          this.readAgreement = true
        }
      }
    },
    compileFormData(confirmed) {
      const keys = [
        'id',
        'title_ja',
        'title_kana',
        'title_en',
        'version_ja',
        'version_kana',
        'version_en',
        'vocal',
        'main_artists',
        'featuring_artists',
        'vocal',
        'is_ai_use',
        'use_rights',
        'freetrack',
        'useCopyrightFreek',
        'hasSoldCopyrightFree',
        'classification',
        'parental_warning',
        'language',
        'copyright_group',
        'copyright_artists',
        'isrc',
        'trial_start_min',
        'trial_start_sec',
        'trial_time_length',
        'remarks_ja',
        'remarks_en',
        'copyright_p_copyright',
        'copyright_p_year',
        'copyright_c_copyright',
        'copyright_c_year',
        'permission_cover_music',
        'companies',
        'monetize_companies',
        'identifiers',
        'identifier_apple',
        'ivk',
        'youtube_monetize_agreed',
        '_restrictions',
      ]
      const params = this.item
      params.confirmed = confirmed
      const formData = new FormData()

      // 再帰的にフォームの要素の追加を行う
      const appendForm = (form, key, value) => {
        if (value == null) {
          form.append(key, '')
        } else if (typeof value === 'object') {
          if (Array.isArray(value)) {
            value.forEach((v, i) => {
              appendForm(form, `${key}[${i}]`, v)
            })
          } else {
            Object.keys(value).forEach(k => {
              appendForm(form, `${key}[${k}]`, value[k])
            })
          }
        } else {
          form.append(key, value)
        }
      }
      Object.keys(params)
        .filter(key => (keys.indexOf(key) !== -1))
        .forEach(key => {
          appendForm(formData, key, params[key])
        })

      // 権利書類の追加
      if (this.item.use_rights === 1 && this.rightsDocuments.length > 0) {
        this.rightsDocuments.forEach((doc, docNum) => {
          formData.append(`rights_documents[${docNum}][type]`, doc.type)
          if (doc.id) {
            formData.append(`rights_documents[${docNum}][id]`, doc.id)
          }
          let counter = 0
          if (doc.showImages) {
            doc.images.forEach((image) => {
              if (!image) return
              if (image.id) {
                formData.append(`rights_documents[${docNum}][saved][${counter}][id]`, image.id)
                counter += 1
              } else if (image.file) {
                formData.append(`rights_documents[${docNum}][files][]`, image.file, image.name)
              }
            })
          }
          if (doc.showUrls) {
            doc.urls.forEach((url) => {
              if (!url) return
              if (url.id) {
                formData.append(`rights_documents[${docNum}][saved][${counter}][id]`, url.id)
                formData.append(`rights_documents[${docNum}][saved][${counter}][path]`, url.path)
                counter += 1
              } else if (url.path) {
                formData.append(`rights_documents[${docNum}][urls][]`, url.path)
              }
            })
          }
        })
      }

      return formData
    },
    // 登録ボタンクリック
    async onRegisterClicked(key = null) {
      if (vTimer) {
        clearTimeout(vTimer)
      }
      const config = { headers: { 'X-CMAP-No-Loading': 1, 'content-type': 'multipart/form-data' } }

      const release = JSON.parse(window.sessionStorage.getItem('album'))

      if (key === null) {
        // 試聴開始位置の文字列整形
        this.item.trial_start_min = parseInt(this.item.trial_start_min, 10) || 0
        this.item.trial_start_sec = parseInt(this.item.trial_start_sec, 10) || 0
        // 各アーティストの国内向け、カナ、海外向けが全て空であれば要素を削除する
        this.item.main_artists = this.item.main_artists.filter(
          (artist, index) => (index === 0 || `${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0),
        )
        this.item.featuring_artists = this.item.featuring_artists.filter(
          artist => (`${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0),
        )
        this.deleteEmptyCopyrightArtist()
      }
      if (this.disableContentId || this.disableContentIdUser) {
        this.item.contents_id_enabled = 0
        const restriction = this.getYoutubeRestriction()
        if (restriction) {
          restriction.settings.contents_id_enabled = this.item.contents_id_enabled
        }
      }
      const formData = this.compileFormData(key)
      try {
        const { discIndex, trackIndex } = this.$route.params
        const { data } = await window.axios.post(`/api/client/products/${release.product.id}/disc/${this.discIndex}/track/${trackIndex}`, formData, config)
        this.item.id = data.id
        this.item.rights_documents = this.item.use_rights === 1 ? data.rights_documents : []
        release.discs[discIndex].tracks[trackIndex] = this.item
        release.product.youtube_monetize_agreed = this.item.youtube_monetize_agreed
        window.sessionStorage.setItem('album', JSON.stringify(release))
        await this.bus.$emit('submit')
        if (key) {
          this.errors = {}
        } else {
          setTimeout(this.onReturnClicked, 500)
        }
      } catch ({ response }) {
        const { errors } = response.data
        if (key && !errors.status) {
          this.realtimeValidation = true
          if (Array.isArray(key)) {
            key.forEach(k => {
              this.$set(this.errors, k, errors[k])
            })
          } else {
            this.$set(this.errors, key, errors[key])
          }
        } else {
          this.realtimeValidation = false
          this.errors = errors
          window.scrollTo(0, 0)
        }
      }
      this.checkTitleInitialHasDigit()
    },
    // バージョン追加クリック
    onVersionAddClicked() {
      this.version_status = true
    },
    // バージョン削除クリック
    onVersionDeleteClicked() {
      this.version_status = false
      this.item.version_ja = null
      this.item.version_kana = null
      this.item.version_en = null
      this.onFieldChanged(['title_ja', 'title_kana', 'title_en'])
    },
    versionContentsCheck() {
      if (this.item.version_ja !== '' && this.item.version_ja !== null) {
        this.version_status = true
      }
    },
    findVersioWords() {
      if (this.item.title_ja.match(/\b(Remix|Remastered|Cover|Live)\b/i) || this.item.title_ja.match(/\bVer\./i)) {
        this.foundVersioWords = true
      } else {
        this.foundVersioWords = false
      }
    },
    // メインアーティスト追加クリック
    onMainArtistAddClicked() {
      this.item.main_artists.push({
        name_ja: '',
        name_kana: '',
        name_en: '',
        type: 1,
      })
    },
    // メインアーティスト削除クリック
    onMainArtistDeleteClicked(index) {
      this.item.main_artists.splice(index, 1)
      if (index < this.releaseArtistsCount) this.releaseArtistsCount -= 1
    },
    onVocaloidDeleteClicked(index, vocaloid) {
      const vocaloidIndex = this.selectedVocaloids.indexOf(vocaloid)
      this.item.main_artists.splice(index, 1)
      this.selectedVocaloids.splice(vocaloidIndex, 1)
    },
    onFeatureVocaloidDeleteClicked(index, vocaloid) {
      const vocaloidIndex = this.selectedFeatureVocaloids.indexOf(vocaloid)
      this.item.featuring_artists.splice(index, 1)
      this.selectedFeatureVocaloids.splice(vocaloidIndex, 1)
    },
    // Featuringアーティスト追加クリック
    onFeaturingArtistAddClicked() {
      this.item.featuring_artists.push({
        prefix: '3', // クライアント画面からのFeaturing接続表記は「feat.」固定
        name_ja: '',
        name_kana: '',
        name_en: '',
        type: 1,
      })
    },
    // Featuringアーティスト削除クリック
    onFeaturingArtistDeleteClicked(index) {
      this.item.featuring_artists.splice(index, 1)
    },
    // 著作権団体追加ボタンクリック
    onCopyrightArtistAddClicked() {
      this.item.copyright_artists.push({
        roles: this.item.copyright_artists.length === 0 ? ['composer'] : [],
        name_ja: '',
        name_kana: '',
        name_en: '',
      })
    },
    // 著作権団体削除ボタンクリック
    onCopyrightArtistDeleteClicked(index) {
      this.item.copyright_artists.splice(index, 1)
    },
    onChangeWithOutVocal() {
      // 作詞のチェックを外す
      const { discIndex, trackIndex } = this.$route.params
      const LyricsErrors = JSON.parse(window.sessionStorage.getItem('hasLyricsError') || '{}')
      LyricsErrors[`${discIndex}-${trackIndex}`] = false
      window.sessionStorage.setItem('hasLyricsError', JSON.stringify(LyricsErrors))
      this.item.copyright_artists.forEach((artist, index) => {
        this.item.copyright_artists[index].roles = artist.roles.filter(
          role => role !== 'lyricist',
        )
      })
      // コピーライトアーティストがからの時は削除
      this.deleteEmptyCopyrightArtist()
    },
    onChangeWithVocal() {
      const { discIndex, trackIndex } = this.$route.params
      const LyricsCompletePercentages = JSON.parse(window.sessionStorage.getItem('lyricsCompletePercentage') || '{}')
      if (LyricsCompletePercentages !== {}
          && LyricsCompletePercentages[`${discIndex}-${trackIndex}`] !== null
          && LyricsCompletePercentages[`${discIndex}-${trackIndex}`] !== 100) {
        const LyricsErrors = JSON.parse(window.sessionStorage.getItem('hasLyricsError') || '{}')
        LyricsErrors[`${discIndex}-${trackIndex}`] = true
        window.sessionStorage.setItem('hasLyricsError', JSON.stringify(LyricsErrors))
      }
    },
    deleteEmptyCopyrightArtist() {
      this.item.copyright_artists = this.item.copyright_artists.filter(
        artist => (`${artist.name_ja}${artist.name_kana}${artist.name_en}`.length > 0 || artist.roles.length > 0),
      )
    },
    getYoutubeRestriction() {
      // eslint-disable-next-line no-underscore-dangle
      const restrictions = this.item._restrictions
      if (restrictions.length) {
        return restrictions.find(item => item.provider.key === 'youtube')
      }
      return false
    },
    durationToSeconds(duration) {
      const [hour, min, sec] = duration.split(':')
      return parseInt(sec, 10) + parseInt(min, 10) * 60 + parseInt(hour, 10) * 60 * 60
    },
    checkTitleInitialHasDigit() {
      this.hasInitialDigit = this.item.title_ja.match(/^[0-9０-９]/)
    },
    isShowMainArtistDeleteButton(artistId = null) {
      const isVocaloid = this.item.main_artists.find((artist) => artist.type === 3
          && artist.id === artistId)
      const hasRequireUserArtistVocaloid = this.item.main_artists
        .find((artist) => artist.require_user_artist === 1)
      const isAlbumArtist = this.albumArtistIds.find(ids => ids === artistId)
      // ユーザーアーティスト必須のボカロがある場合に残り一つのユーザーアーティストの場合削除させない
      if (!isVocaloid
          && hasRequireUserArtistVocaloid
          && this.item.main_artists.filter(artist => artist.type !== 3).length === 1) {
        return false
      }
      // アルバム選択アーティストの場合に残り一つのアルバム選択アーティストの場合に削除させない
      if (isAlbumArtist
          && this.item.main_artists
            .filter(artist => this.albumArtistIds
              .find(ids => ids === artist.id)).length === 1) {
        return false
      }

      // メインアーティストが残り１つの場合は削除ボタンは非表示(Various Artistsで有効になる)
      if (this.item.main_artists.length === 1) {
        return false
      }
      return true
    },
    showVocaloidListPopUp(isFeaturing = false) {
      this.isSelectedUserArtist = this.item.main_artists.some(artist => artist.type === 1)
      if (isFeaturing) {
        this.isFeaturing = true
      }
      this.popUpDialogShown = true
    },
    closeModel() {
      this.popUpDialogShown = false
    },

    modalConfirm(event) {
      if (this.isFeaturing) {
        event.forEach(e => {
          if (this.selectedFeatureVocaloids.filter(vocaloid => vocaloid.id === e.id).length === 0) {
            e.prefix = '3'
            e.type = 3
            this.selectedFeatureVocaloids.push(e)
            this.item.featuring_artists.push(e)
          }
        })
      } else {
        event.forEach(e => {
          if (this.selectedVocaloids.filter(vocaloid => vocaloid.id === e.id).length === 0) {
            e.type = 3
            this.selectedVocaloids.push(e)
            this.item.main_artists.push(e)
          }
        })
      }
      this.isFeaturing = false
      this.popUpDialogShown = false
    },
    itemToBeDeleted(event) {
      if (this.isFeaturing) {
        event.forEach(element => {
          this.selectedFeatureVocaloids.forEach(element1 => {
            if (element1.id === element.id) {
              const index = this.selectedFeatureVocaloids.indexOf(element1)
              const index1 = this.item.featuring_artists.indexOf(element1)
              this.selectedFeatureVocaloids.splice(index, 1)
              this.item.featuring_artists.splice(index1, 1)
            }
          })
        })
      } else {
        event.forEach(element => {
          this.selectedVocaloids.forEach(element1 => {
            if (element1.id === element.id) {
              const index = this.selectedVocaloids.indexOf(element1)
              const index1 = this.item.main_artists.indexOf(element1)
              this.selectedVocaloids.splice(index, 1)
              this.item.main_artists.splice(index1, 1)
            }
          })
        })
      }
    },
    onChangeClassification(value) {
      if (value !== 2) {
        if (this.versionAddedCover) {
          this.item.version_ja = (this.item.version_ja.replace('カバー', '')).trim()
          this.item.version_kana = this.item.version_kana.replace('カバー', '')
          this.item.version_en = (this.item.version_en.replace('cover', '')).trim()
          this.versionAddedCover = false
          if (this.item.version_ja === '') this.version_status = false
        }
      } else if (this.item.version_ja === null) {
        this.item.version_ja = 'カバー'
        this.item.version_kana = 'カバー'
        this.item.version_en = 'cover'
        this.version_status = true
        this.versionAddedCover = true
      } else if (this.item.version_ja.indexOf('カバー') === -1 && this.item.version_ja.toLowerCase().indexOf('cover') === -1) {
        this.item.version_ja = (`${this.item.version_ja} カバー`).trim()
        if (this.item.version_kana.indexOf('カバー') === -1) this.item.version_kana += 'カバー'
        if (this.item.version_en.indexOf('cover') === -1) this.item.version_en = (`${this.item.version_en} cover`).trim()
        this.version_status = true
        this.versionAddedCover = true
      }
      // カバーアレンジの場合に権利書類設定
      if ([2, 3].includes(value)) {
        this.item.permission_cover_music = 1
        this.item.use_rights = 1
        if (this.rightsDocuments.length === 0) {
          this.rightsDocuments.push({
            type: 0,
            images: [],
            urls: [],
          })
        }
      } else {
        // それ以外の時は「カバー・アレンジ許諾」をクリア
        this.item.permission_cover_music = ''
      }
      this.onFieldChanged('classification')
    },
    checkUseRights(value) {
      if (value !== 0) {
        // YouTubeマネタイズサービス を未選択の状態にする
        this.enableContentId = false
      }
      this.item.use_rights = value
    },
  },
}
</script>

<style scoped>
@media screen and (max-width: 895px) {
  .c-btn--right {
    margin-top: 0px;
    margin-bottom: 30px;
  }

  .form-group-wrap__btn-wrap {
    height: 120px;
  }
}

@media screen and (max-width: 895px) {
  .form-group-wrap__btn-wrap {
    height: 50px;
  }
}

@media screen and (max-width: 895px) {
  .form-group-wrap__vocalo-btn-wrap {
    height: 160px;
    margin: 18px 0 0 0;
  }
  .form-group-wrap__vocalo-btn-wrap >.c-btn {
    margin: 14px 0 0 0;
  }
}

@media screen and (min-width: 896px) {
  .copyright-form-control {
    width: 80%;
  }
}

.required-composr::after {
  background-color: #c0c0c0;
}

@media screen and (min-width: 896px) {
  .not-first-space:first-child {
    padding-top: 0px;
  }
}

.agree-area {
  width: 100%;
  height: 300px;
  overflow-y: scroll;
  border: 1px inset #808080;
  background-color: white;
  margin: auto auto;
  padding: 5px 2px 5px 2px;
  text-align: left;
}

.agree-area A {
  text-decoration: underline;
}

.agree-area A:hover {
  text-decoration: none;
}

.asterisk {
  color: #ff4b6d;
  font-weight: bold;
}

LI.list {
  margin-left: 25px;
  padding-left: -15px;
  list-style-type: disc;
}

.apple ::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 8px;
}

.apple ::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
.vocaloid-image-container{
  width: 160px;
  display: flex;
  justify-content: center;
}
.disabled{
  opacity: 0.5;
}
</style>
